import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Snackbar,
  Alert,
  Typography,
  Grid,
  FormControlLabel,
  Switch,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DirectionsCar as CarIcon,
  Speed as SpeedIcon,
  Event as EventIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { 
  Card, 
  Badge, 
  Button as BsButton,
  Row,
  Col,
  Container,
  Form
} from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import {
  getAllCars,
  createCar,
  updateCar,
  deleteCar,
  toggleCarAvailability,
  getAvailableCars
} from '../api/cars';

const CarManagement = () => {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCar, setEditingCar] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const [formData, setFormData] = useState({
    name: '',
    model: '',
    year: new Date().getFullYear(),
    transmission: 'Automatic',
    price: '',
    image: '',
    features: '',
    available: true,
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [pickupDate, setPickupDate] = useState('');
  const [returnDate, setReturnDate] = useState('');

  useEffect(() => {
    fetchCars();
  }, []);

  const fetchCars = async () => {
    try {
      setLoading(true);
      let data;
      if (pickupDate && returnDate) {
        data = await getAvailableCars(pickupDate, returnDate);
      } else {
        data = await getAllCars();
      }
      setCars(data);
    } catch (error) {
      showSnackbar('Error fetching cars', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (car = null) => {
    if (car) {
      setEditingCar(car);
      setFormData({
        name: car.name,
        model: car.model,
        year: car.year,
        transmission: car.transmission,
        price: car.price,
        image: car.image,
        features: Array.isArray(car.features) ? car.features.join(', ') : '',
        available: car.available,
      });
      setImageFile(null);
      setImagePreview(car.image ? `${process.env.REACT_APP_API_URL}${car.image}` : null); 
    } else {
      setEditingCar(null);
      setFormData({
        name: '',
        model: '',
        year: new Date().getFullYear(),
        transmission: 'Automatic',
        price: '',
        image: '',
        features: '',
        available: true,
      });
      setImageFile(null);
      setImagePreview(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCar(null);
  };

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'available' ? checked : value,
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('model', formData.model);
      formDataToSend.append('year', formData.year);
      formDataToSend.append('transmission', formData.transmission);
      formDataToSend.append('price', formData.price);
      formDataToSend.append('available', formData.available);
      
      const featuresArray = formData.features.split(',').map((f) => f.trim()).filter((f) => f);
      formDataToSend.append('features', JSON.stringify(featuresArray));
      
      if (imageFile) {
        formDataToSend.append('image', imageFile);
      }

      if (editingCar) {
        await updateCar(editingCar._id, formDataToSend);
        showSnackbar('Car updated successfully', 'success');
      } else {
        await createCar(formDataToSend);
        showSnackbar('Car created successfully', 'success');
      }

      handleCloseDialog();
      fetchCars();
    } catch (error) {
      console.error('Submit error:', error);
      showSnackbar('Error saving car', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this car?')) {
      try {
        await deleteCar(id);
        showSnackbar('Car deleted successfully', 'success');
        fetchCars();
      } catch (error) {
        showSnackbar('Error deleting car', 'error');
      }
    }
  };

  const handleToggleAvailability = async (id) => {
    try {
      await toggleCarAvailability(id);
      showSnackbar('Car availability updated', 'success');
      fetchCars();
    } catch (error) {
      showSnackbar('Error updating availability', 'error');
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ bgcolor: '#f4f6f8', minHeight: '100vh', p: 3 }}>
      {/* Header Section */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 4,
        p: 3,
        bgcolor: '#1a2238',
        borderRadius: 2,
        boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
        transition: 'all 0.3s ease'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CarIcon sx={{ mr: 2, fontSize: 40, color: '#ffffff' }} />
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#ffffff', letterSpacing: '-0.5px' }}>
              Car Management
            </Typography>
            <Typography variant="body2" sx={{ color: '#d1d5db' }}>
              Manage your vehicle inventory with ease
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          size="large"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ 
            px: 4, 
            py: 1.5,
            bgcolor: '#3b82f6',
            color: '#ffffff',
            fontWeight: 600,
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
            transition: 'all 0.3s ease',
            '&:hover': { 
              bgcolor: '#2563eb',
              boxShadow: '0 8px 24px rgba(59, 130, 246, 0.5)',
              transform: 'translateY(-2px)'
            }
          }}
        >
          Add New Car
        </Button>
      </Box>

      {/* Date range filter UI for admin */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          label="Pickup Date (DD/MM/YYYY)"
          value={pickupDate}
          onChange={e => setPickupDate(e.target.value)}
          size="small"
        />
        <TextField
          label="Return Date (DD/MM/YYYY)"
          value={returnDate}
          onChange={e => setReturnDate(e.target.value)}
          size="small"
        />
        <Button onClick={fetchCars} disabled={loading} variant="contained">Check Availability</Button>
      </Box>

      {/* Loading State */}
      {loading ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '400px',
          bgcolor: '#ffffff',
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          <Typography variant="h6" sx={{ color: '#6b7280' }}>
            ⏳ Loading cars...
          </Typography>
        </Box>
      ) : cars.length === 0 ? (
        <Box sx={{ 
          textAlign: 'center', 
          py: 10,
          bgcolor: '#ffffff',
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          <CarIcon sx={{ fontSize: 100, color: '#d1d5db', mb: 3 }} />
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#1a2238' }}>
            No cars available
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: '#6b7280' }}>
            Start by adding your first vehicle to the inventory
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ 
              px: 4, 
              py: 1.5,
              bgcolor: '#3b82f6',
              borderRadius: '12px',
              '&:hover': { 
                bgcolor: '#2563eb',
                transform: 'translateY(-2px)'
              }
            }}
          >
            Add Your First Car
          </Button>
        </Box>
      ) : (
        <Container fluid>
          <Row className="g-4">
            {cars.map((car) => (
              <Col xs={12} sm={6} md={6} lg={4} xl={3} key={car._id}>
                <Card 
                  className="h-100 border-0"
                  style={{
                    borderRadius: '16px',
                    overflow: 'hidden',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                    background: '#ffffff'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.15)';
                  }}
                >
                  {/* Badge */}
                  <div style={{ position: 'relative' }}>
                    <Badge 
                      bg={car.available ? 'success' : 'danger'}
                      style={{
                        position: 'absolute',
                        top: '16px',
                        right: '16px',
                        zIndex: 10,
                        padding: '8px 14px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                        borderRadius: '20px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      {car.available ? '✓ Available' : '✗ Unavailable'}
                    </Badge>
                    
                    {/* Car Image */}
                    <div style={{ 
                      height: '220px', 
                      background: car.image 
                        ? `url('${process.env.REACT_APP_API_URL }${car.image}') center/cover no-repeat`
                        : 'linear-gradient(135deg, #1a2238 0%, #2d3748 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'transform 0.4s ease',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.03)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    >
                      {!car.image && (
                        <div style={{ textAlign: 'center', color: '#d1d5db' }}>
                          <CarIcon style={{ fontSize: 70, opacity: 0.7 }} />
                          <div style={{ fontSize: '1.1rem', fontWeight: 600, marginTop: '8px' }}>
                            {car.name}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Gradient Overlay with Car Name */}
                    {car.image && (
                      <div style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: 'linear-gradient(to top, rgba(26, 34, 56, 0.9) 0%, transparent 100%)',
                        padding: '30px 16px 12px',
                      }}>
                        <h4 style={{
                          color: '#ffffff',
                          fontWeight: 700,
                          margin: 0,
                          textShadow: '0 2px 6px rgba(0,0,0,0.6)',
                          fontSize: '1.4rem'
                        }}>
                          {car.name}
                        </h4>
                      </div>
                    )}
                  </div>

                  <Card.Body className="d-flex flex-column p-4">
                    {/* Car Name - Show if no image */}
                    {!car.image && (
                      <h4 className="mb-3" style={{ fontWeight: 700, color: '#1a2238' }}>
                        {car.name}
                      </h4>
                    )}
                    
                    {/* Model & Year */}
                    <div className="d-flex gap-3 mb-3 pb-3 border-bottom" style={{ borderColor: '#e5e7eb' }}>
                      <div className="flex-fill">
                        <small className="text-muted d-block mb-1" style={{ fontSize: '0.7rem', fontWeight: 600 }}>
                          MODEL
                        </small>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1a2238' }}>
                          {car.model}
                        </div>
                      </div>
                      <div className="flex-fill">
                        <small className="text-muted d-block mb-1" style={{ fontSize: '0.7rem', fontWeight: 600 }}>
                          YEAR
                        </small>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1a2238' }}>
                          <EventIcon style={{ fontSize: 16, marginRight: 4, color: '#3b82f6' }} />
                          {car.year}
                        </div>
                      </div>
                    </div>

                    {/* Transmission */}
                    <div className="mb-3">
                      <Badge 
                        bg="primary" 
                        className="px-3 py-2"
                        style={{ fontSize: '0.75rem', fontWeight: 600, backgroundColor: '#3b82f6 !important' }}
                      >
                        <SpeedIcon style={{ fontSize: 14, marginRight: 5 }} />
                        {car.transmission}
                      </Badge>
                    </div>

                    {/* Features */}
                    {car.features && car.features.length > 0 && (
                      <div className="mb-3">
                        <small className="text-muted d-block mb-2" style={{ fontSize: '0.7rem', fontWeight: 600 }}>
                          FEATURES
                        </small>
                        <div className="d-flex flex-wrap gap-1">
                          {car.features.slice(0, 3).map((feature, index) => (
                            <Badge 
                              key={index}
                              bg="light" 
                              text="dark"
                              className="border"
                              style={{ 
                                fontSize: '0.7rem', 
                                fontWeight: 500, 
                                padding: '6px 10px',
                                backgroundColor: '#f3f4f6 !important',
                                borderColor: '#d1d5db !important'
                              }}
                            >
                              {feature}
                            </Badge>
                          ))}
                          {car.features.length > 3 && (
                            <Badge 
                              bg="light" 
                              text="dark"
                              className="border"
                              style={{ 
                                fontSize: '0.7rem', 
                                fontWeight: 500, 
                                padding: '6px 10px',
                                backgroundColor: '#f3f4f6 !important',
                                borderColor: '#d1d5db !important'
                              }}
                            >
                              +{car.features.length - 3} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Price Box */}
                    <div 
                      className="mt-auto text-center p-4"
                      style={{
                        background: 'linear-gradient(135deg, #1a2238 0%, #2d3748 100%)',
                        borderRadius: '12px',
                        color: '#ffffff',
                        boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      <div style={{
                        position: 'absolute',
                        top: '-15px',
                        right: '-15px',
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.1)'
                      }} />
                      <div style={{
                        position: 'absolute',
                        bottom: '-20px',
                        left: '-20px',
                        width: '80px',
                        height: '80px',
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.1)'
                      }} />
                      
                      <div style={{ position: 'relative', zIndex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '6px', marginBottom: '4px' }}>
                          <MoneyIcon style={{ fontSize: 26, marginBottom: '4px' }} />
                          <span style={{ fontSize: '2.2rem', fontWeight: 800, letterSpacing: '-0.5px' }}>
                            {Number(car.price).toLocaleString()}
                          </span>
                        </div>
                        <div style={{ fontSize: '0.9rem', fontWeight: 600, opacity: 0.9 }}>
                          FCFA / day
                        </div>
                      </div>
                    </div>
                  </Card.Body>

                  {/* Footer Actions */}
                  <Card.Footer 
                    className="d-flex justify-content-between align-items-center p-3"
                    style={{ background: '#f3f4f6', borderTop: '1px solid #e5e7eb' }}
                  >
                    <div className="d-flex gap-2">
                      <BsButton 
                        variant="primary" 
                        size="sm"
                        className="rounded-circle p-2"
                        style={{ 
                          width: '38px', 
                          height: '38px',
                          backgroundColor: '#3b82f6',
                          border: 'none',
                          transition: 'all 0.3s ease',
                          '&:hover': { backgroundColor: '#2563eb' }
                        }}
                        onClick={() => handleOpenDialog(car)}
                      >
                        <EditIcon style={{ fontSize: 16, color: '#ffffff' }} />
                      </BsButton>
                      <BsButton 
                        variant="danger" 
                        size="sm"
                        className="rounded-circle p-2"
                        style={{ 
                          width: '38px', 
                          height: '38px',
                          backgroundColor: '#ef4444',
                          border: 'none',
                          transition: 'all 0.3s ease',
                          '&:hover': { backgroundColor: '#dc2626' }
                        }}
                        onClick={() => handleDelete(car._id)}
                      >
                        <DeleteIcon style={{ fontSize: 16, color: '#ffffff' }} />
                      </BsButton>
                    </div>
                    <Form.Check 
                      type="switch"
                      id={`switch-${car._id}`}
                      label={
                        <small style={{ fontWeight: 600, color: car.available ? '#16a34a' : '#6b7280' }}>
                          {car.available ? <CheckIcon style={{ fontSize: 16, marginRight: 4 }} /> : <CancelIcon style={{ fontSize: 16, marginRight: 4 }} />}
                          {car.available ? 'Active' : 'Inactive'}
                        </small>
                      }
                      checked={car.available}
                      onChange={() => handleToggleAvailability(car._id)}
                      className="d-flex align-items-center"
                    />
                  </Card.Footer>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      )}

      {/* Add/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: '#1a2238',
            color: '#ffffff',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            transition: 'all 0.3s ease'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: '#111827',
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 2,
          px: 3
        }}>
          <CarIcon />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {editingCar ? '✏️ Edit Car' : '➕ Add New Car'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, bgcolor: '#1a2238' }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Car Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                InputLabelProps={{ style: { color: '#d1d5db' } }}
                InputProps={{ style: { color: '#ffffff', backgroundColor: '#2d3748', borderRadius: '8px' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Model"
                name="model"
                value={formData.model}
                onChange={handleInputChange}
                required
                InputLabelProps={{ style: { color: '#d1d5db' } }}
                InputProps={{ style: { color: '#ffffff', backgroundColor: '#2d3748', borderRadius: '8px' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Year"
                name="year"
                type="number"
                value={formData.year}
                onChange={handleInputChange}
                required
                InputLabelProps={{ style: { color: '#d1d5db' } }}
                InputProps={{ style: { color: '#ffffff', backgroundColor: '#2d3748', borderRadius: '8px' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#d1d5db' }}>Transmission</InputLabel>
                <Select
                  name="transmission"
                  value={formData.transmission}
                  onChange={handleInputChange}
                  label="Transmission"
                  sx={{ color: '#ffffff', bgcolor: '#2d3748', borderRadius: '8px', '& .MuiSvgIcon-root': { color: '#ffffff' } }}
                >
                  <MenuItem value="Automatic">Automatic</MenuItem>
                  <MenuItem value="Manual">Manual</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Price (FCFA/day)"
                name="price"
                type="number"
                value={formData.price}
                onChange={handleInputChange}
                required
                InputLabelProps={{ style: { color: '#d1d5db' } }}
                InputProps={{ style: { color: '#ffffff', backgroundColor: '#2d3748', borderRadius: '8px' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.available}
                    onChange={handleInputChange}
                    name="available"
                    sx={{ 
                      '& .MuiSwitch-track': { bgcolor: '#4b5563' },
                      '& .MuiSwitch-thumb': { bgcolor: formData.available ? '#3b82f6' : '#6b7280' }
                    }}
                  />
                }
                label={<Typography sx={{ color: '#d1d5db' }}>Available</Typography>}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ 
                p: 2.5, 
                border: '2px dashed #4b5563',
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.05)'
              }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#d1d5db', mb: 1.5 }}>
                  📷 Car Image
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  size="large"
                  sx={{ 
                    py: 1.5,
                    borderColor: '#4b5563',
                    color: '#d1d5db',
                    borderRadius: '8px',
                    '&:hover': {
                      borderColor: '#3b82f6',
                      bgcolor: 'rgba(59, 130, 246, 0.1)'
                    }
                  }}
                >
                  {imageFile ? `📁 ${imageFile.name}` : '🖼️ Choose Image File'}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </Button>
                {imagePreview && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '220px', 
                        borderRadius: '12px',
                        border: '2px solid #4b5563',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                      }}
                    />
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#9ca3af' }}>
                      Image preview
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Features (comma-separated)"
                name="features"
                value={formData.features}
                onChange={handleInputChange}
                placeholder="5 seats, GPS, Air Conditioning, Bluetooth, USB Port"
                multiline
                rows={3}
                helperText="Separate each feature with a comma"
                InputLabelProps={{ style: { color: '#d1d5db' } }}
                InputProps={{ style: { color: '#ffffff', backgroundColor: '#2d3748', borderRadius: '8px' } }}
                sx={{ '& .MuiFormHelperText-root': { color: '#9ca3af' } }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: '#111827' }}>
          <Button 
            onClick={handleCloseDialog}
            size="large"
            sx={{ 
              px: 3, 
              color: '#d1d5db', 
              borderRadius: '8px',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            size="large"
            sx={{ 
              px: 4, 
              bgcolor: '#3b82f6',
              borderRadius: '8px',
              '&:hover': { 
                bgcolor: '#2563eb',
                transform: 'translateY(-2px)'
              }
            }}
          >
            {editingCar ? '✓ Update Car' : '✓ Create Car'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ 
            bgcolor: snackbar.severity === 'success' ? '#16a34a' : '#ef4444',
            color: '#ffffff',
            '& .MuiAlert-icon': { color: '#ffffff' }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CarManagement;